/**
 * 매장 메뉴 관련 API 훅
 *
 * @see SPEC-192-i18n-api.md - Accept-Language 헤더 지원
 */
import { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import {
  storeApi,
  getErrorMessage,
  ApiClientError,
  type MenuStoreDetail,
  type Menu,
  type MenuCategory,
} from '@/lib/api';
import { useLocale, useCurrency } from '@/contexts/AppContext';
import { logger } from '@/utils/logger';

interface UseStoreMenusState {
  store: MenuStoreDetail | null;
  categories: MenuCategory[];
  menus: Menu[];
  loading: boolean;
  error: Error | null;
}

interface UseStoreMenusReturn extends UseStoreMenusState {
  refetch: () => Promise<void>;
  getMenusByCategory: (categoryId: number) => Menu[];
}

interface UseStoreMenusOptions {
  category?: string;
}

const MENU_LIST_PAGE_SIZE = 100;

/**
 * 매장별 메뉴 목록 조회 훅
 * @param storeParam 매장 슬러그 또는 ID
 * @param options 필터 옵션
 *
 * #194: store 필드에 매장 상세 정보 포함 (MenuStoreDetail)
 *   - address, hours, status, organization, brand 등
 *   - 별도의 매장 API 호출 불필요
 */
export function useStoreMenus(
  storeParam: string | undefined,
  options: UseStoreMenusOptions = {}
): UseStoreMenusReturn {
  const { locale } = useLocale();
  const { currency } = useCurrency();
  const category = options.category;

  const abortControllerRef = useRef<AbortController | null>(null);

  const [state, setState] = useState<UseStoreMenusState>({
    store: null,
    categories: [],
    menus: [],
    loading: true,
    error: null,
  });

  const owner = useMemo(() => ({ storeParam, category, locale, currency }),
    [storeParam, category, locale, currency]);
  const activeOwner = useRef<typeof owner | null>(null);
  useLayoutEffect(() => {
    activeOwner.current = owner;
    setState({ store: null, categories: [], menus: [], loading: Boolean(storeParam), error: null });
    return () => {
      activeOwner.current = null;
      abortControllerRef.current?.abort();
    };
  }, [owner, storeParam]);

  const fetchMenus = useCallback(async () => {
    if (activeOwner.current !== owner) return;
    abortControllerRef.current?.abort();
    if (!storeParam) {
      setState({
        store: null,
        categories: [],
        menus: [],
        loading: false,
        error: null,
      });
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const isCurrent = () => activeOwner.current === owner &&
      abortControllerRef.current === controller && !controller.signal.aborted;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // AppContext syncs apiClient Accept-Language; this keeps locale changes refetching menus.
      void locale;
      const baseParams = {
        category,
        currency,
        // #276: 시간 기반 비가용(영업시간 외 등) 메뉴도 목록에 포함시키고(filter_available=false),
        // 각 메뉴의 가용성 정보(is_available_at)를 함께 받아(include_availability=true)
        // "영업시간 외" 등의 오버레이를 표시. BE 계약: MenuResource.php:177, MenuApiTest.php:725-751.
        filter_available: false,
        include_availability: true,
        per_page: MENU_LIST_PAGE_SIZE,
      };
      const firstResponse = await storeApi.getMenus(storeParam, {
        ...baseParams,
        page: 1,
      }, controller.signal);

      if (!isCurrent()) return;
      const lastPage = Math.max(1, firstResponse.meta?.last_page ?? 1);

      // #688: 첫 페이지로 last_page 를 안 뒤 2..N 페이지를 병렬 로드(직렬 waterfall 회피, codex).
      // 메뉴 100개 초과 매장(prod store 10=153/store 12=242)에서 늦은 display_order 카테고리
      // (Cócteles/Tequila/Vodka 등 주류 섹션)가 page2+ 에 있어 누락되던 문제 시정.
      const restPages = lastPage > 1
        ? await Promise.all(
            Array.from({ length: lastPage - 1 }, (_unused, index) =>
              storeApi.getMenus(storeParam, { ...baseParams, page: index + 2 }, controller.signal),
            ),
          )
        : [];

      if (!isCurrent()) return;

      const allMenus = [
        ...firstResponse.data.menus,
        ...restPages.flatMap((response) => response.data.menus),
      ];

      setState({
        store: firstResponse.data.store,
        categories: firstResponse.data.categories,
        menus: allMenus,
        loading: false,
        error: null,
      });
    } catch (err) {
      if (!isCurrent()) return;

      logger.error('[useStoreMenus] API 호출 실패:', err);

      const isNotFound = err instanceof ApiClientError && err.status === 404;
      const message = isNotFound
        ? '매장을 찾을 수 없습니다'
        : getErrorMessage(err, '메뉴 목록을 불러오는데 실패했습니다');
      setState((prev) => ({
        ...prev,
        loading: false,
        error: new Error(message),
      }));
    }
  }, [storeParam, category, locale, currency, owner]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  const getMenusByCategory = useCallback(
    (categoryId: number) => {
      return state.menus.filter((menu) => menu.category_id === categoryId);
    },
    [state.menus]
  );

  return {
    ...state,
    refetch: fetchMenus,
    getMenusByCategory,
  };
}
